import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateAttributePipe } from '../../../../pipes/translate-attribute/translate-attribute.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, of, timeout } from 'rxjs';
import { HandleBookingService } from '../../../services/handle-booking.service';
import { ParkingRepo } from '../../../repo/parking.repo';
import { BookingConfigService } from '../../../services/booking-config.service';
import { FormService } from '../../features/booking/components/form/service';
import { CartService } from '../../../../services/cart.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';

@Component({
  standalone: true,
  selector: 'app-additional-services',
  imports: [
    CommonModule,
    TranslocoModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    TranslateAttributePipe,
    TitleCasePipe
  ],
  templateUrl: './edit-booking-additional-services.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-booking-additional-services.component.css']
})
export class AdditionalServicesComponent implements OnChanges {
  @Input() booking: any;
  additionalServicesAvailable: any[] = [];
  parkings: any;
  @Input() site: string = '';
  @Output() servicesChanged = new EventEmitter<void>();

  // UI State
  unselectedServiceIds: string[] = [];
  originallyHadServices: boolean = false;
  pendingServices: any[] = [];
  bookingDetails: any = {
    order: null,
    parking: null,
    shuttles: [],
    additionalServices: []
  };
  isLoading: boolean = false;
  bookingLoaded: boolean = false;

  // Loading & API
  isLoadingParkings: boolean = false;
  selectedParking = new BehaviorSubject<any>(null);
  showRetryButton = false;
  lastApiParams: { periodFrom: string, periodTo: string, discountCode: string } | null = null;

  // Route & Booking params
  email: string | null = "";
  bookingNumber: string | null = "";
  orderId: any = null;

  // Language & Preferences
  activeLang: string = 'sv';
  checkInBagage: any[] = [];

  // Form and Parking
  handleBookingGroup: any;
  form: any;
  details: any;
  parkingGroup: any;
  departureHotel: any;
  arrivalHotel: any;
  parkingType: string = '';
  parkingFrom: any;
  arrivalParkingOptions: any[] = [];

  // Additional Services & Products
  showSubscription: boolean = false;
  previousPrice: number = 0;
  currentPrice: number = 0;
  amountForSelectedAdditionalServices: number = 0;
  departureFlight: any;
  arrivalFlight: any;
  formattedDeparture: string = '';
  formattedArrival: string = '';
  showDepHotel: boolean = false;
  showArrHotel: boolean = false;
  expandedDescriptions: Set<string> = new Set();

  // Tenant Configuration
  tentantConfigSortingParking: any = {
    'skavsta': (parkings: any[]) => {
      []
      if (parkings?.length > 1) {
        const firstItem = parkings.shift();
        parkings.push(firstItem);
      }
      return parkings;
    }
  };
  orgId: string;
  hasSpecialParams: boolean = false;

  constructor(
    private _snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private route: ActivatedRoute,
    private router: Router,
    private handleBookingService: HandleBookingService,
    private parkingRepo: ParkingRepo,
    private bookingConfigService: BookingConfigService,
    private _formService: FormService,
    private cartService: CartService
  ) { }

  public ngOnInit() {
    this.isLoading = true;
    this.getPreservedQueryParams();
    this.activeLang = this.translocoService.getActiveLang();
    this.translocoService.langChanges$.subscribe((lang) => {
      this.activeLang = lang;
    });
    this.translocoService.selectTranslate('PARKIFLY.PLACEHOLDER.1_HOUR_BEFORE')
      .subscribe(value => {
        this.checkInBagage = [
          { value: '1', viewValue: this.translocoService.translate("PARKIFLYFORM.YES") },
          { value: '0', viewValue: this.translocoService.translate("PARKIFLY.SUMMARY.ONLY_CARRYON_LUGGAGE_OPTION") },
        ]
      });

    this.route.paramMap.subscribe(params => {
      this.email = params.get("email");
      this.bookingNumber = params.get("booking_number");
      if (this.email && this.bookingNumber) {
        this.handleBookingService.getBookingDetails(this.email, this.bookingNumber).subscribe(
          {
            next: ((bookingDetails: any) => {

              this.site = bookingDetails.organizationName?.toLowerCase();
              if (this.site == "flygets.se") {
                this.site = "flygets";
                this.orgId = 'A848CCD3-FD8D-4035-925E-571EBB4E93E8';
              }
              if (this.site == "tui") {
                this.orgId = '2868EF69-054C-40BD-93AE-85CF08E4C71F';
              } else if (this.site == "nazar") {
                this.orgId = '3868EF69-054C-40BD-93AE-85CF08E4C71F';
              }
              if (this.site == "tui" || this.site == "nazar" || this.site == "ving") {
                this.site = "flygets";
              }

              this.bookingConfigService.setOrganization(this.site);
              this.handleBookingService.initForm(this.site);
              this.handleBookingGroup = this.handleBookingService.getForm().get('details');
              this._formService.initForm(this.site);

              //pdf details
              this.parkingType = this._formService.selectedParking;
              this.form = this._formService;
              this.details = this.form.getForm().get('details');
              this.parkingGroup = this.form.getForm().get('parkingGroup');
              this.departureHotel = this.form.departureHotel || this.form.getForm().get('hotels')?.controls?.departureHotel?.value;
              this.arrivalHotel = this.form.arrivalHotel || this.form.getForm().get('hotels')?.controls?.arrivalHotel?.value;

              if (bookingDetails.orderStatus == "Cancelled") {
                const message = "#" + this.bookingNumber + " " + this.translocoService.translate('FLYGETS.SELFSERVICE.CANCELLED_ORDER');
                this._snackBar.open(message, 'OK', {
                  duration: 3000,
                  verticalPosition: 'top'
                });
                this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
              }
              this.orderId = bookingDetails.orderId;
              this.booking = bookingDetails;
              this.showDepHotel = this.booking?.departure?.departureCode ? false : true;
              this.showArrHotel = this.booking?.arrival?.arrivalCode ? false : true;
              this.showSubscription = (bookingDetails?.parkingProductType?.toLowerCase() == "subscription") ? true : false;

              if (this.booking != null) {
                setTimeout(() => {
                  this.bookingLoaded = true;
                }, 1000);
              }

              this.handleBookingService.getForm().get('details').controls['cellular'].setValue(bookingDetails.cellular ?? '');
              this.handleBookingService.getForm().get('details').controls['regNr'].setValue(bookingDetails.carRegNumber ?? '');
              this.handleBookingService.getForm().get('details').controls['email'].setValue(bookingDetails.email ?? '');
              this.handleBookingService.getForm().get('details').controls['email'].disable();
              if (this.site === 'flygets') {
                this.handleBookingService.getForm().get('details').controls['comment'].setValue(bookingDetails.comment ?? '');
                this.handleBookingService.getForm().get('details').controls['contact'].setValue("false");
                this.handleBookingService.getForm().get('details').controls['persons'].setValue(bookingDetails.passengers.toString());
                this.parkingFrom = bookingDetails.parkingFrom;
                this.arrivalParkingOptions = this.parkingRepo.createParkingStartTimeOptions(this.parkingFrom);
                this.parkingFrom = new Date(this.parkingFrom);
                var parkingShuttleFrom = new Date(bookingDetails.parkingShuttleFrom);
                var currentParkingTime = (
                  parkingShuttleFrom.getHours().toString().length <= 1 ? '0' + parkingShuttleFrom.getHours() : parkingShuttleFrom.getHours()) + ':' +
                  (parkingShuttleFrom.getMinutes().toString().length <= 1 ? '0' + parkingShuttleFrom.getMinutes() : parkingShuttleFrom.getMinutes()
                  );
                this.handleBookingService.getForm().get('details').controls['arrivalTimeAtParking'].setValue(currentParkingTime);

                const additionalServicesArray = bookingDetails?.additionalServices?.length > 0
                  ? bookingDetails.additionalServices.filter((e: any) => (e.status != 'Cancelled' && e.status != 'Removed'))
                  : [];
                if (additionalServicesArray.length > 0) {
                  additionalServicesArray.forEach(function (element: any, index: any) {
                    if (typeof element.name === 'string') {
                      element.name = JSON.parse(element.name);
                    }
                    if (typeof element.description === 'string') {
                      element.description = JSON.parse(element.description);
                    }
                    if (element.amount && element.vatAmount) {
                      element.amount = element.amount + element.vatAmount;
                    }
                  });
                }
                this.bookingDetails.additionalServices = additionalServicesArray;
                this.bookingDetails.additionalProducts = bookingDetails.additionalProducts;
                this.checkAvailableParkingsForOrganization(this.booking.parkingFromActual, this.booking.parkingTo, this.booking.discountCode ?? 'NONE');
              }
              this.isLoading = false;
            }),
            error: (error: any) => {
              this.isLoading = false;
              const message = this.translocoService.translate('FLYGETS.SELFSERVICE.VALIDATION_NOK');
              this._snackBar.open(message, 'OK', {
                duration: 3000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
              });
              this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
            }
          }
        );
      } else {
        this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bookingDetails && this.bookingDetails) {
      this.bookingDetails.additionalServices = this.bookingDetails.additionalServices || [];
      this.originallyHadServices = this.bookingDetails.additionalServices.length > 0;
    }
  }

  getSelectedServices(): any[] {
    return this.booking?.additionalServices?.filter((service: any) => {
      return service && service.status !== 'Cancelled' && service.status !== 'Removed';
    }) || [];
  }

  getAvailableServices(): any[] {
    const selectedNames = this.getSelectedServices().map((service: any) => {
      if (!service?.name) {
        return null;
      }
      try {
        return typeof service.name === 'string' ? JSON.parse(service.name).en : service.name.en;
      } catch {
        return service.name;
      }
    }).filter((name: any) => !!name);

    return (this.additionalServicesAvailable || []).filter((service: any) => {
      if (!service?.name) {
        return true;
      }
      let serviceName: any;
      try {
        serviceName = typeof service.name === 'string' ? JSON.parse(service.name).en : service.name.en;
      } catch {
        serviceName = service.name;
      }
      return !selectedNames.includes(serviceName);
    });
  }

  onFormChanged(): void {
    this.servicesChanged.emit();
  }

  checkAvailableParkingsForOrganization(periodFrom: string, periodTo: string, discountCode: string) {
    this.isLoadingParkings = true;
    this.showRetryButton = false;
    this.lastApiParams = { periodFrom, periodTo, discountCode };
    const message = this.translocoService.translate('HANDLE_BOOKING.CHECK_AVAILABLE_PARKING');
    this._snackBar.open(message, undefined, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
    this.cartService
      .getAvailableParkingsOnEdit(this.orderId, periodFrom, periodTo, discountCode)
      .pipe(
        timeout(30000),
        catchError(error => {
          this.isLoadingParkings = false;
          setTimeout(() => {
            this.showRetryButton = true;
          }, 0);
          console.error('API call failed:', error);
          this.parkings = this.parkings || [];
          return of(null);
        })
      )
      .subscribe((parkings: any) => {
        this.isLoadingParkings = false;
        this.showRetryButton = false;
        if (this.booking?.organizationName?.toLocaleLowerCase() == 'tui' || this.booking?.organizationName?.toLocaleLowerCase() == 'nazar') {
          this.parkings = parkings?.parkings.filter((parking: any) => {
            return parking.id == '8c98315a-b2ad-4e70-86f9-5d54992fed26';
          })
        } else {
          this.parkings = parkings?.parkings?.reverse();
        }

        if (this.tentantConfigSortingParking[this.booking?.organizationName?.toLowerCase()]) {
          this.parkings = this.tentantConfigSortingParking[this.booking?.organizationName?.toLowerCase()](this.parkings);
        }

        const parkingSite: any = this.booking?.parking?.toLowerCase();
        const parking: any = parkings?.parkings?.filter((parking: any) => parking?.name?.toLowerCase() == parkingSite);
        this.additionalServicesAvailable = (parking && parking[0]?.additionalServices) || [];
        if (this.additionalServicesAvailable.length <= 0) {
          const message = "No additional services available for the selected date range.";
          this._snackBar.open(message, 'OK', {
            duration: 3000,
            verticalPosition: 'top',
            horizontalPosition: 'center'
          });
          this.routerNavigatePreservingParams(['/parkifly/booking-overview', { email: this.email, booking_number: this.bookingNumber }]);
        }
      });
  }

  formatDate(sourceDate: Date): string {
    var twoDigitMonth = ((sourceDate.getMonth() + 1) >= 10) ? (sourceDate.getMonth() + 1) : '0' + (sourceDate.getMonth() + 1);
    var twoDigitDate = ((sourceDate.getDate()) >= 10) ? (sourceDate.getDate()) : '0' + (sourceDate.getDate());
    var twoDigitHour = ((sourceDate.getHours()) >= 10) ? (sourceDate.getHours()) : '0' + (sourceDate.getHours());
    var twoDigitMinute = ((sourceDate.getMinutes()) >= 10) ? (sourceDate.getMinutes()) : '0' + (sourceDate.getMinutes());
    var returnDate = sourceDate.getFullYear() + "-" + twoDigitMonth + "-" + twoDigitDate + 'T' + twoDigitHour + ':' + twoDigitMinute + ':00';
    return returnDate;
  }

  getShuttleDate(flightDateStr: string, hasBaggage: any) {
    if (!flightDateStr) return '';
    const flightDate = new Date(flightDateStr);
    const minutesToAdd = (hasBaggage == 'true' || hasBaggage === 1) ? 45 : 25;
    flightDate.setMinutes(flightDate.getMinutes() + minutesToAdd);
    return this.formatDate(flightDate);
  };

  getParkingFromDate(form: any): string {
    const departureType = this.handleBookingGroup.controls['departureType']?.value;

    if (departureType === 'true') {
      const flightDate = this.form?.selectedDepartureFlight?.value?.departureTime?.scheduled ? this.form?.selectedDepartureFlight?.value?.departureTime?.scheduled : this.booking.departure?.scheduledDeparture;
      const timeAtParking = form.details.departureTimeAtParking ? form.details.departureTimeAtParking : (this.bookingDetails?.parkingShuttleFrom ? this.bookingDetails.parkingShuttleFrom : '');
      if (flightDate && timeAtParking) {
        const date = new Date(flightDate).toISOString().split('T')[0];
        return `${date}T${timeAtParking}:00`;
      }
    }

    return (this.handleBookingGroup.controls.departureDate.value).replace(' | ', 'T') + ':00';
  }

  getParkingToDate(form: any): string {
    const arrivalType = this.handleBookingGroup.controls['arrivalType']?.value;

    if (arrivalType === 'true') {
      const flightDate = this.form?.selectedArrivalFlight?.value?.arrivalTime?.scheduled ? this.form?.selectedArrivalFlight?.value?.arrivalTime?.scheduled : this.booking.arrival?.scheduledArrival;
      const timeAtParking = form.details.arrivalTimeAtParking ? form.details.arrivalTimeAtParking : (this.bookingDetails?.parkingShuttleTo ? this.bookingDetails.parkingShuttleTo : '');
      if (flightDate && timeAtParking) {
        const date = new Date(flightDate).toISOString().split('T')[0];
        return `${date}T${timeAtParking}:00`;
      }
    }

    return (this.handleBookingGroup.controls.arrivalDate.value).replace(' | ', 'T') + ':00';
  }

  toggleEditServices(): void {
    // if (this.showEditServices) {
    this.bookingDetails.additionalServices = this.bookingDetails.additionalServices.filter((service: any) => {
      return !this.unselectedServiceIds.includes(service.id);
    });
    this.unselectedServiceIds = [];
    this.onFormChanged();
    // }
    // this.showEditServices = !this.showEditServices;
    // if (this.showEditServices && this.parkings && this.parkings[0]) {
    //   this.additionalServicesAvailable = this.parkings[0]?.additionalServices || [];
    // }
  }

  cancelAllAdditionalServices(): void {
    this.bookingDetails.additionalServices = [];
    this.onFormChanged();
  }

  getServiceName(nameJson: any): string {
    try {
      const parsed = typeof nameJson === 'string' ? JSON.parse(nameJson) : nameJson;
      return parsed[this.translocoService.getActiveLang()] || parsed['en'] || nameJson;
    } catch {
      return nameJson;
    }
  }

  getServiceDescription(descriptionJson: any): string {
    try {
      const parsed = typeof descriptionJson === 'string' ? JSON.parse(descriptionJson) : descriptionJson;
      return parsed[this.translocoService.getActiveLang()] || parsed['en'] || descriptionJson;
    } catch {
      return descriptionJson;
    }
  }

  isServiceSelected(service: any): boolean {
    let orderServiceNameEn = '';
    try {
      const orderNameParsed = JSON.parse(service.name);
      orderServiceNameEn = orderNameParsed.en;
    } catch {
      return false;
    }

    const isSelected = this.bookingDetails.additionalServices.some((bookingService: any) => {
      const bookingServiceName = typeof bookingService.name === 'string'
        ? JSON.parse(bookingService.name).en
        : bookingService.name.en;
      return bookingServiceName === orderServiceNameEn;
    });

    const isPending = this.pendingServices?.some((pendingService: any) => {
      return pendingService.name.en === orderServiceNameEn;
    });

    return isSelected || isPending;
  }

  isServicePreselected(service: any): boolean {
    if (!this.originallyHadServices || !service || !this.booking?.additionalServices?.length) {
      return false;
    }

    let orderServiceNameEn = '';
    try {
      orderServiceNameEn = typeof service.name === 'string' ? JSON.parse(service.name).en : service.name.en;
    } catch {
      return false;
    }

    return this.booking.additionalServices.some((bookingService: any) => {
      const bookingServiceName = typeof bookingService.name === 'string'
        ? JSON.parse(bookingService.name).en
        : bookingService.name.en;
      return bookingServiceName === orderServiceNameEn && bookingService.status !== 'Cancelled' && bookingService.status !== 'Removed';
    });
  }

  isBookingServiceSelected(service: any): boolean {
    return !this.unselectedServiceIds.includes(service.id);
  }

  isBookingSubmissionOfKey(service: any): boolean {
    return service.name.en === 'Submission of key';
  }

  hasOtherBookingServicesSelected(): boolean {
    return this.bookingDetails.additionalServices.some((s: any) => {
      return s.name.en !== 'Submission of key' && !this.unselectedServiceIds.includes(s.id);
    });
  }

  onBookingServiceToggle(service: any, event: any): void {
    const serviceNameEn = service.name.en;
    const isSubmissionOfKey = serviceNameEn === 'Submission of key';

    if (!event.checked && isSubmissionOfKey) {
      const hasOtherServicesSelected = this.bookingDetails.additionalServices.some((s: any) => {
        return s.name.en !== 'Submission of key' && !this.unselectedServiceIds.includes(s.id);
      });

      if (hasOtherServicesSelected) {
        event.source.checked = true;
        return;
      }
    }

    if (!event.checked) {
      if (!this.unselectedServiceIds.includes(service.id)) {
        this.unselectedServiceIds.push(service.id);
      }
    } else {
      this.unselectedServiceIds = this.unselectedServiceIds.filter(id => id !== service.id);
      this.onFormChanged();

      if (!isSubmissionOfKey) {
        const submissionService = this.bookingDetails.additionalServices.find((s: any) => {
          return s.name.en === 'Submission of key';
        });

        if (submissionService && this.unselectedServiceIds.includes(submissionService.id)) {
          this.unselectedServiceIds = this.unselectedServiceIds.filter(id => id !== submissionService.id);
        }
      }
    }

    this.onFormChanged();
  }

  onServiceToggle(service: any, event: any): void {
    if (!this.pendingServices) {
      this.pendingServices = [];
    }
    if (!this.bookingDetails?.additionalServices) {
      this.bookingDetails.additionalServices = [];
    }

    const serviceNameEn = JSON.parse(service.name).en;
    const isSubmissionOfKey = serviceNameEn === 'Submission of key';

    const isExistingService = this.originallyHadServices && this.booking?.additionalServices?.some((s: any) => {
      const existingName = typeof s.name === 'string' ? JSON.parse(s.name).en : s.name.en;
      return existingName === serviceNameEn && s.status !== 'Cancelled' && s.status !== 'Removed';
    });

    if (!event.checked && isExistingService) {
      this._snackBar.open('Cannot remove individual services. Use "Cancel Additional Services" to remove all services first.', 'OK', {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      event.source.checked = true;
      return;
    }

    if (!event.checked && isSubmissionOfKey) {
      const hasOtherServices = this.pendingServices.some((s: any) => {
        return s.name.en !== 'Submission of key';
      });

      if (hasOtherServices) {
        event.source.checked = true;
        return;
      }
    }

    if (event.checked) {
      const exists = this.pendingServices.some((s: any) => {
        return s.name.en === serviceNameEn;
      });

      if (!exists) {
        const parsedService = {
          ...service,
          name: JSON.parse(service.name)
        };
        this.pendingServices.push(parsedService);
      }

      if (!isSubmissionOfKey) {
        const submissionService = this.additionalServicesAvailable?.find((s: any) => {
          const name = JSON.parse(s.name).en;
          return name === 'Submission of key';
        });

        if (submissionService) {
          const submissionExistsInPending = this.pendingServices.some((s: any) => {
            return s.name.en === 'Submission of key';
          });

          const submissionExistsInExisting = this.booking?.additionalServices?.some((s: any) => {
            const serviceName = typeof s.name === 'string' ? JSON.parse(s.name).en : s.name.en;
            return serviceName === 'Submission of key' && s.status !== 'Cancelled' && s.status !== 'Removed';
          });

          if (!submissionExistsInPending && !submissionExistsInExisting) {
            const parsedSubmissionService = {
              ...submissionService,
              name: JSON.parse(submissionService.name)
            };
            this.pendingServices.push(parsedSubmissionService);
          }
        }
      }
    } else {
      this.pendingServices = this.pendingServices.filter((s: any) => {
        return s.name.en !== serviceNameEn;
      });
    }

    const originalServices = this.originallyHadServices ?
      (this.booking?.additionalServices?.filter((e: any) => (e.status !== 'Cancelled' && e.status !== 'Removed')) || []).map((service: any) => {
        const parsedService = { ...service };
        if (typeof service.name === 'string') {
          parsedService.name = JSON.parse(service.name);
        }
        return parsedService;
      }) : [];

    this.bookingDetails.additionalServices = [...originalServices, ...this.pendingServices];
    this.getSelectedServicesTotal();
    this.onFormChanged();
  }

  getSelectedServicesTotal(): number {
    this.amountForSelectedAdditionalServices = this.pendingServices.reduce((total, service) => {
      return total + (service.totalPrice || 0);
    }, 0);
    return this.amountForSelectedAdditionalServices;
  }

  updateBooking() {
    this.isLoading = true;
    const form = this.handleBookingService.getForm().value;
    var updates;

    if (this.site === 'flygets' || this.site == 'tui' || this.site === 'nazar') {

      const arrivalFlightInfo = this.arrivalFlight;
      const departureFlightInfo = this.departureFlight;

      let departureFlightnr = departureFlightInfo ? departureFlightInfo.flightId : this.booking.departure?.departureCode ?? null;
      let departureFlightdate = departureFlightInfo?.departureTime?.scheduled
        ? (departureFlightInfo?.departureTime.scheduled)
        : (this.booking.departure?.scheduledDeparture ?? undefined);
      let departureFlightdeparturedate = departureFlightInfo ? departureFlightInfo?.flightLegIdentifier?.flightDepartureDateUtc : (this.booking.departure?.scheduledDeparture ? (this.booking.departure?.scheduledDeparture?.replace(/T.*$/, 'T00:00:00')) : null);
      let departureFlightterminal = departureFlightInfo ? departureFlightInfo.locationAndStatus?.terminal : this.booking.departure?.terminalName ?? null;

      let arrivalFlightnr = arrivalFlightInfo ? arrivalFlightInfo.flightId : this.booking.arrival?.arrivalCode ?? null;
      let arrivalFlightdate = arrivalFlightInfo?.arrivalTime?.scheduled ? (arrivalFlightInfo?.arrivalTime.scheduled) : (this.booking.arrival?.scheduledArrival ?? undefined);
      let arrivalFlightdeparturedate = arrivalFlightInfo ? arrivalFlightInfo?.flightLegIdentifier?.flightDepartureDateUtc : (this.booking.arrival?.scheduledArrival ? (this.booking.arrival?.scheduledArrival?.replace(/T.*$/, 'T00:00:00')) : null);
      let arrivalFlightterminal = arrivalFlightInfo ? arrivalFlightInfo.locationAndStatus?.terminal : this.booking.arrival?.terminalName ?? null;

      if (this.showDepHotel) {
        departureFlightnr = null;
        departureFlightdate = null;
        departureFlightdeparturedate = null;
        departureFlightterminal = null;
      }

      if (this.showArrHotel) {
        arrivalFlightnr = null;
        arrivalFlightdate = null;
        arrivalFlightdeparturedate = null;
        arrivalFlightterminal = null;
      }

      const arrivalHotel = this.handleBookingGroup.get('arrivalHotel')?.value || this.booking.arrival?.terminalName || form.hotels?.arrivalHotel;
      const departureHotel = this.handleBookingGroup.get('departureHotel')?.value || this.booking.departure?.terminalName || form.hotels?.departureHotel;

      let departureTerminal = departureHotel || departureFlightterminal;
      let arrivalTerminal = arrivalHotel || arrivalFlightterminal;

      var timeFromForm = form.details.arrivalTimeAtParking;
      var hoursFromForm = timeFromForm.charAt(0) == 0 ? timeFromForm.substring(1, 2) : timeFromForm.substring(0, 2);
      var minutesFromForm = timeFromForm.charAt(3) == 0 ? timeFromForm.substring(4) : timeFromForm.substring(3);
      var newParkingFrom = new Date(this.parkingFrom);
      newParkingFrom.setHours(hoursFromForm, minutesFromForm);
      // if (this.parkingFrom < newParkingFrom) {
      //   newParkingFrom.setDate(newParkingFrom.getDate() - 1);
      // }
      updates = {
        orderNumber: this.bookingNumber ?? '',
        orderId: this.orderId ?? '',
        email: this.email ? this.email : '',
        customer: {
          cellular: form.details.cellular,
          comment: form.details.comment,
          contact: form.details.contact ? (form.details.contact == 'true' ? true : false) : false,
          discountCode: form.details.discount,
        },
        parking: {
          carId: form.details.regNr,
          parkingFrom: this.booking?.parkingFromActual,
          parkingTo: this.booking?.parkingTo,
          passengers: parseInt(form.details.persons)
        },
        shuttles: [
          {
            stationcode: 'ARN',
            shuttletypecode: 'flygets-p1-airport-transfer',
            productcode: 'SHUTDEP',
            shuttledate: this.booking?.parkingShuttleFrom ? this.booking.parkingShuttleFrom : this.booking?.parkingFrom,
            flightnr: departureFlightnr,
            flightdate: departureFlightdate,
            flightdeparturedate: departureFlightdeparturedate,
            flightterminal: departureFlightterminal,
            terminal: departureTerminal,
            passengers: form.details.persons,
            childseats: 0,
            baggage: this.handleBookingGroup.controls.baggage?.value == 'true' ? 1 : 0,
            comment: form.details.comment
          },
          {
            stationcode: 'ARN',
            shuttletypecode: 'flygets-p1-parking-transfer',
            productcode: 'SHUTARR',
            shuttledate: this.booking?.parkingShuttleTo ? this.booking.parkingShuttleTo : this.booking?.parkingTo,
            flightnr: arrivalFlightnr,
            flightdate: arrivalFlightdate,
            flightdeparturedate: arrivalFlightdeparturedate,
            flightterminal: arrivalFlightterminal,
            terminal: arrivalTerminal,
            passengers: form.details.persons,
            childseats: 0,
            baggage: this.handleBookingGroup.controls.baggage?.value == 'true' ? 1 : 0,
            comment: form.details.comment
          }
        ]
      }
    }

    if (this.bookingDetails?.additionalServices) {
      updates.additionalServices = this.bookingDetails?.additionalServices;
    }

    const totalAmountToPay = this.getSelectedServicesTotal();

    this.handleBookingService.updateOrderByOrderNumberEmail(updates).subscribe(result => {
      this.isLoading = false;

      if (result.result !== 'DetailsRetrieved') {
        this._snackBar.open(result.result, 'OK', {
          duration: 3000,
          verticalPosition: 'top',
          horizontalPosition: 'center'
        });
        return;
      }

      if ((totalAmountToPay > 0) || (this.booking.orderStatus?.toLowerCase() == "checkoutadditional")) {
        this.routerNavigatePreservingParams(['parkifly', this.site, 'checkout', 'payment', this.orderId]);
      } else {
        this.routerNavigatePreservingParams(['/parkifly/booking-overview', { email: this.email, booking_number: this.bookingNumber }]);
      }
      // this.formHasChanges = false;
      this.handleBookingGroup.markAsPristine();
      const message = this.translocoService.translate('HANDLE_BOOKING.UPDATE_BOOKING');
      this._snackBar.open(message, 'OK', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    });
  }

  goToCancelServices() {
    const prefix = this.router.url.startsWith('/parkifly-new') ? 'parkifly-new' : 'parkifly';
    this.routerNavigatePreservingParams([prefix, 'cancel-additional-services', { email: this.email, booking_number: this.bookingNumber }]);
  }

  private getPreservedQueryParams(): Record<string, string> {
    const params = this.route.snapshot.queryParamMap;
    const queryParams: Record<string, string> = {};
    const lang = params.get('lang');
    const mobile = params.get('mobile');

    if (lang) {
      queryParams.lang = lang;
    }
    if (mobile) {
      queryParams.mobile = mobile;
    }

    this.hasSpecialParams = !!(lang || mobile);

    return queryParams;
  }

  private routerNavigatePreservingParams(commands: any[], extras: Record<string, any> = {}) {
    const preserved = this.getPreservedQueryParams();
    const mergedQueryParams = {
      ...(extras.queryParams || {}),
      ...preserved
    };

    if (Object.keys(mergedQueryParams).length > 0) {
      extras = { ...extras, queryParams: mergedQueryParams };
    }

    this.router.navigate(commands, extras);
  }

  goBack() {
    if (this.booking.orderStatus?.toLowerCase() == "checkoutadditional") {
      this.routerNavigatePreservingParams(['parkifly', this.site, 'checkout', 'payment', this.orderId]);
    } else {
      this.routerNavigatePreservingParams(['parkifly/booking-overview', { email: this.email, booking_number: this.bookingNumber }]);
    }
  }

  goToBookingSummary() {
    this.routerNavigatePreservingParams(['parkifly/booking-overview', { email: this.email, booking_number: this.bookingNumber }]);
  }

  goToPaymentPage() {
    const totalAmountToPay = this.getSelectedServicesTotal();
    if (totalAmountToPay > 0) {
      this.routerNavigatePreservingParams(['parkifly', this.site, 'checkout', 'payment', this.orderId]);
    } else {
      this.routerNavigatePreservingParams(['/parkifly/booking-overview', { email: this.email, booking_number: this.bookingNumber }]);
    }
  }

  toggleDescription(serviceId: string): void {
    if (this.expandedDescriptions.has(serviceId)) {
      this.expandedDescriptions.delete(serviceId);
    } else {
      this.expandedDescriptions.add(serviceId);
    }
  }

  isDescriptionExpanded(serviceId: string): boolean {
    return this.expandedDescriptions.has(serviceId);
  }

}
